import { FrontendLogger } from '@biz-nest/core-client';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { Eventbus } from '../utils/eventbus';

/**
 * Sets up and manages event subscriptions with automatic cleanup.
 * This function creates a subscription to the event bus and automatically unsubscribes
 * when the cleanup function is called, preventing memory leaks.
 *
 * @template T - The type of data expected in the event handlers
 * @param {Eventbus} eventbus - The event bus instance to subscribe to
 * @param {Record<string, (data: T) => void>} handlers - An object mapping event types to their respective handlers
 * @returns {{ cleanup: () => void }} An object with a cleanup function to unsubscribe all handlers
 *
 * @example
 * // Setup event handlers
 * const handlers = {
 *   'USER_LOGGED_IN': (userData) => console.log('User logged in:', userData),
 *   'USER_LOGGED_OUT': () => console.log('User logged out')
 * };
 *
 * // Initialize subscriptions
 * const { cleanup } = runSubscriptionsForSharedUse(eventbus, handlers);
 *
 * // Later, to clean up
 * cleanup();
 */
export const runSubscriptionsForSharedUse = (
  eventbus: Eventbus,
  handlers: Record<string, (data) => void>,
  byPassDistinct = false
): { cleanup: () => void } => {
  const destroyed$ = new Subject<boolean>();

  /**
   * Sets up the event bus subscription with automatic cleanup
   * @private
   */
  const runSubscriptions = (): void => {
    eventbus.generalEvent$
      .pipe(
        takeUntil(destroyed$),
        distinctUntilChanged((prev, curr) => {
          if (byPassDistinct) {
            return false;
          }
          const compared = (prev?.type === curr?.type) && (JSON.stringify(prev?.data) === JSON.stringify(curr?.data));

          FrontendLogger.debug('runSubscriptionsForSharedUse', 'distinctUntilChanged', compared);

          return compared;
        })
      )
      .subscribe((event) => {
        // Only process events with data or boolean data (including false)
        if ((event?.data !== undefined) || (event && typeof event.data === 'boolean')) {
          const handler = handlers[event.type];

          if (typeof handler === 'function') {
            handler(event.data);
          }
        }
      });
  };

  // Initialize the subscriptions
  runSubscriptions();

  return {
    /**
     * Cleans up all subscriptions by completing the destroy subject
     */
    cleanup: (): void => {
      destroyed$.next(true);
      destroyed$.complete(); // TODO skeptical
    }
  };
};
